import { useCallback, useState } from 'react';
import { getEnterpriseTechnology } from '../../../../services/commonService';
import { ENVIRONMENT_TYPE } from '../../../../config/env';
import { makeAiEnablementSyncCall, makeDataSourcesSyncCall } from '../solutionApiService';
import { mergeAgentSyncData, mergeSyncResponses } from '../solutionUtils';
import { markSolutionSynced, setAgentCacheEntry } from '../solutionCache';

interface UseManualSyncOptions {
  aiSolutionData: any;
  selectedSolutionId: string | number | null;
  currentOrg: string;
  diagramId?: string;
  processType?: string;
  setAgentLlmResponse: (data: any[]) => void;
  setLlmResponseData: (data: any[]) => void;
  setUpdatedSolutionData: (fn: (prev: any) => any) => void;
  setAiSolutionData?: (data: any) => void;
  setLlmResponseLoading: (value: boolean) => void;
}

interface UseManualSyncResult {
  isManualSyncLoading: boolean;
  agentClickCount: number;
  lastClickTime: number;
  triggerManualSync: () => Promise<void>;
  handleAgentTabClick: (tabKey: string) => void;
  resetAdminSyncState: () => void;
}

export const useManualSync = ({
  aiSolutionData,
  selectedSolutionId,
  currentOrg,
  diagramId,
  processType,
  setAgentLlmResponse,
  setLlmResponseData,
  setUpdatedSolutionData,
  setAiSolutionData,
  setLlmResponseLoading,
}: UseManualSyncOptions): UseManualSyncResult => {
  const [isManualSyncLoading, setIsManualSyncLoading] = useState(false);
  const [agentClickCount, setAgentClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  const resetAdminSyncState = useCallback(() => {
    setAgentClickCount(0);
    setLastClickTime(0);
    setIsManualSyncLoading(false);
  }, []);

  const triggerManualSync = useCallback(async () => {
    if (!aiSolutionData || !selectedSolutionId || isManualSyncLoading) return;

    const solutionId = String(selectedSolutionId);
    if (String(aiSolutionData.id) !== solutionId) {
      console.warn(
        `⚠️ Manual sync: Solution ID mismatch (${aiSolutionData.id} !== ${selectedSolutionId}). Aborting.`
      );
      return;
    }

    setIsManualSyncLoading(true);
    console.log('🔄 Admin manual sync triggered');

    try {
      const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
      const additionalPayload = {
        llm_agent: aiSolutionData?.llm_agent,
        solution_enabler: aiSolutionData?.solution_enabler,
        diagram_id: diagramId,
        organization_id: currentOrg,
        type: ENVIRONMENT_TYPE,
      };

      // Step 1 — data sources
      const dataSourcesResponse = await makeDataSourcesSyncCall(
        aiSolutionData,
        enterpriseTechResponse,
        additionalPayload,
        processType
      );
      const firstResponseData =
        dataSourcesResponse?.application_in_use || dataSourcesResponse?.ai_solution || [];

      setLlmResponseData(firstResponseData);
      setUpdatedSolutionData((prev: any) => ({
        ...(prev || {}),
        agent: { ...(prev?.agent || {}), ai_solution: firstResponseData },
      }));
      setLlmResponseLoading(false);

      // Step 2 — AI enablement
      const enablementResponse = await makeAiEnablementSyncCall(
        aiSolutionData,
        enterpriseTechResponse,
        additionalPayload,
        processType
      );

      const combined = mergeSyncResponses(dataSourcesResponse, enablementResponse);
      const agentResponseData =
        combined?.agent_classification ||
        combined?.ai_enablement?.enablement_matrix?.embedded ||
        combined?.embedded ||
        [];

      setAgentLlmResponse(agentResponseData);
      setLlmResponseData(combined?.ai_solution || combined?.application_in_use || []);

      setUpdatedSolutionData((prev: any) => {
        const merged = mergeAgentSyncData(prev || aiSolutionData, combined);
        setAiSolutionData?.(merged);

        const hasComplete =
          merged?.agent?.ai_enablement &&
          (merged?.agent?.ai_solution || merged?.agent?.application_in_use);

        if (hasComplete) {
          setAgentCacheEntry(solutionId, {
            solutionData: merged,
            agentData: {
              agentLlmResponse: agentResponseData,
              llmResponseData: combined?.ai_solution || combined?.application_in_use || [],
            },
            agentUpdatedAt: merged?.agent?.updated_at ?? null,
          });
          markSolutionSynced(solutionId, true, true);
        }
        return merged;
      });

      console.log('✅ Admin manual sync completed');
    } catch (error) {
      console.error('❌ Admin manual sync failed:', error);
      setLlmResponseLoading(false);
    } finally {
      setIsManualSyncLoading(false);
    }
  }, [
    aiSolutionData,
    currentOrg,
    diagramId,
    isManualSyncLoading,
    processType,
    setAgentLlmResponse,
    setAiSolutionData,
    setLlmResponseData,
    setLlmResponseLoading,
    setUpdatedSolutionData,
    selectedSolutionId,
  ]);

  /** Handles tab clicks; triggers manual sync on the 3rd consecutive click within 2 s */
  const handleAgentTabClick = useCallback(
    (tabKey: string) => {
      const currentTime = Date.now();

      if (tabKey === 'agents') {
        if (currentTime - lastClickTime > 2000) {
          setAgentClickCount(1);
        } else {
          const newCount = agentClickCount + 1;
          setAgentClickCount(newCount);
          if (newCount === 3) {
            console.log('🚀 Admin sync: Triple-click detected!');
            triggerManualSync();
            setAgentClickCount(0);
          }
        }
        setLastClickTime(currentTime);
      } else {
        if (agentClickCount > 0) setAgentClickCount(0);
      }
    },
    [agentClickCount, lastClickTime, triggerManualSync]
  );

  return {
    isManualSyncLoading,
    agentClickCount,
    lastClickTime,
    triggerManualSync,
    handleAgentTabClick,
    resetAdminSyncState,
  };
};
